import { captureException } from '@sentry/browser';

type StorageOperation = 'read' | 'write' | 'remove';

function reportStorageError(error: unknown, storageKey: string, operation: StorageOperation): void {
  try {
    const err = error instanceof Error ? error : new Error(String(error));
    captureException(err, {
      tags: {
        module: 'localStorageAdapter',
        operation,
        storageKey,
      },
    });
  } catch {
    /* Sentry must never break storage paths */
  }
}

/**
 * Reads JSON from localStorage and parses it as a partial object. On failure, reports to
 * Sentry and returns null. Does not remove the key on parse errors.
 */
export function readObjectFromLocalStorage<T extends object>(storageKey: string): Partial<T> | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw === null) {
      return null;
    }
    return JSON.parse(raw) as Partial<T>;
  } catch (error) {
    reportStorageError(error, storageKey, 'read');
    return null;
  }
}

/**
 * Serializes a value to JSON and writes it to localStorage. Returns true on success,
 * false on failure (never throws).
 */
export function writeObjectToLocalStorage<T extends object>(storageKey: string, value: T): boolean {
  try {
    localStorage.setItem(storageKey, JSON.stringify(value));
    return true;
  } catch (error) {
    reportStorageError(error, storageKey, 'write');
    return false;
  }
}

/**
 * Removes an entry from localStorage (by key). Returns true on success, false on failure (never throws).
 */
export function removeLocalStorage(storageKey: string): boolean {
  try {
    localStorage.removeItem(storageKey);
    return true;
  } catch (error) {
    reportStorageError(error, storageKey, 'remove');
    return false;
  }
}
